import type { ReactNode } from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'
import { SITE_NAME } from '~shared/seo'
import { clientSiteOrigin } from '@/lib/siteOrigin'

export type ForumSeoProps = {
  title: string
  description: string
  noindex?: boolean
  ogType?: 'website' | 'article'
  jsonLd?: Record<string, unknown> | Record<string, unknown>[]
}

function canonicalPath(pathname: string): string {
  const p = pathname || '/'
  if (p === '/') return '/'
  return p.replace(/\/+$/, '') || '/'
}

export function ForumSeo({
  title,
  description,
  noindex = false,
  ogType = 'website',
  jsonLd,
}: ForumSeoProps): ReactNode {
  const { pathname } = useLocation()
  const base = clientSiteOrigin()
  const canonPath = canonicalPath(pathname)
  const canonicalUrl = `${base}${canonPath === '/' ? '/' : canonPath}`
  const safeTitle = title.trim() || SITE_NAME
  const scripts: ReactNode =
    jsonLd ?
      (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).map((obj, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))
    : null

  const robotsMeta = (
    <>
      {!noindex && (
        <meta
          name="robots"
          content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
        />
      )}
      {noindex && <meta name="robots" content="noindex, follow" />}
    </>
  )

  return (
    <Helmet prioritizeSeoTags>
      <html lang="en" />
      <title>{safeTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      {robotsMeta}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={safeTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={safeTitle} />
      <meta name="twitter:description" content={description} />
      {scripts}
    </Helmet>
  )
}
